import { paths } from '@/config/paths'
import { getBorrowMaxValue } from '@/domain/action-max-value-getters/getBorrowMaxValue'
import { formatPercentage } from '@/domain/common/format'
import { useAccount } from '@/domain/hooks/useAccount'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { getValidateBorrowArgs, validateBorrow } from '@/domain/market-validators/validateBorrow'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { borrowDialogConfig } from '@/features/dialogs/borrow/BorrowDialog'
import { getTokenImage } from '@/ui/assets/tokens'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { PointsBadge } from '@/ui/molecules/points-badge/PointsBadge'
import { TokenSymbolEnum, getAddressBySymbol } from '@/utils/tokens'
import { usePrivy } from '@privy-io/react-auth'
import { ArrowRight, HelpCircle } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useChainId } from 'wagmi'

export function BorrowUsdxl() {
  const chainId = useChainId()
  const account = useAccount()
  const { connectWallet } = usePrivy()
  const openDialog = useOpenDialog()
  const router = useRouter()
  const { marketInfo } = useMarketInfo({ chainId })
  const { getByAddress } = usePointsData()

  const usdxlAddress = CheckedAddress(getAddressBySymbol(TokenSymbolEnum.USDXL))
  const usdxlReserve = marketInfo.findOneReserveByUnderlyingAsset(usdxlAddress)
  const usdxlToken = usdxlReserve.token

  const borrowValidationArgs = getValidateBorrowArgs(NormalizedUnitNumber(0), usdxlReserve, marketInfo)
  const validationIssue = validateBorrow(borrowValidationArgs)
  const availableToBorrow = getBorrowMaxValue({
    validationIssue,
    user: borrowValidationArgs.user,
    asset: borrowValidationArgs.asset,
    facilitatorBorrowLimit: marketInfo.facilitator.availableToBorrow,
  })

  const position = marketInfo.userPositions.find((p) => p.reserve.token.symbol === usdxlToken.symbol)
  const currentDebt = position?.borrowBalance ?? NormalizedUnitNumber(0)

  const borrowAPY = Percentage(usdxlReserve.variableBorrowApy ?? 0, true, true)
  const hasCollateral = marketInfo.userPositionSummary.totalCollateralUSD.gt(0)

  const borrowPoints = getByAddress({
    type: 'pooled',
    action: 'borrow',
    address: usdxlReserve.token.address,
  })

  const handleBorrow = () => {
    openDialog(borrowDialogConfig, { token: usdxlToken })
  }

  return (
    <Panel.Wrapper className="overflow-hidden border-primary/20 bg-panel">
      <div className="flex flex-col gap-6 p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <Typography variant="h3" gradient className="text-xl">
              Borrow USDXL
            </Typography>
            <Typography variant="p" className="text-muted-foreground leading-relaxed">
              Borrow USDXL against your pooled collateral in HypurrFi Pooled.
            </Typography>
          </div>
        </div>

        {account ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-4 rounded-lg border border-white/5 bg-white/[.02] p-4 sm:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <Typography variant="prompt" className="text-muted-foreground text-sm">
                  Available to borrow
                </Typography>
                <div className="flex items-center gap-1.5">
                  <Image
                    src={getTokenImage(TokenSymbol('USDXL'))}
                    className="h-5 w-5 rounded-full"
                    width={20}
                    height={20}
                    alt="USDXL"
                  />
                  <Typography variant="p" className="font-semibold text-lg" gradient>
                    {USD_MOCK_TOKEN.format(availableToBorrow, { style: 'compact' })}
                  </Typography>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Typography variant="prompt" className="text-muted-foreground text-sm">
                  Borrow APY
                </Typography>
                <div className="flex items-center gap-2">
                  <Typography variant="p" className="font-semibold text-lg" gradient>
                    {formatPercentage(borrowAPY)}
                  </Typography>
                  {borrowPoints?.multiplier && (
                    <PointsBadge
                      asset={TokenSymbol(TokenSymbolEnum.USDXL)}
                      action="borrow"
                      multiplier={borrowPoints.multiplier}
                      size="sm"
                    />
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Typography variant="prompt" className="text-muted-foreground text-sm">
                  Your USDXL debt
                </Typography>
                <div className="flex items-center gap-1.5">
                  <Image
                    src={getTokenImage(TokenSymbol('USDXL'))}
                    className="h-5 w-5 rounded-full"
                    width={20}
                    height={20}
                    alt=""
                  />
                  <Typography variant="p" className="font-semibold text-lg">
                    {currentDebt.gt(0) ? currentDebt.toFixed(2) : '0'}
                  </Typography>
                </div>
              </div>

              {hasCollateral ? (
                <div className="flex items-center sm:justify-end">
                  <Button size="sm" onClick={handleBorrow}>
                    Borrow USDXL
                  </Button>
                </div>
              ) : (
                <div className="flex items-center sm:justify-end">
                  <Button size="sm" onClick={() => router.push(paths.earn)}>
                    Deposit Collateral
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            {hasCollateral ? (
              <button
                type="button"
                onClick={() =>
                  router.push(
                    paths.pooledDetails.replace(':chainId', chainId.toString()).replace(':asset', usdxlAddress),
                  )
                }
                className="flex items-center gap-1 text-muted-foreground text-sm transition-colors hover:text-foreground"
              >
                View USDXL market details
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <Typography variant="span" className="text-muted-foreground text-sm">
                <HelpCircle className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
                To borrow USDXL, you need to deposit collateral first. Deposit HYPE or other supported assets in
                HypurrFi Pooled to get started.
              </Typography>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4 rounded-lg border border-white/5 bg-white/[.02] p-4">
              <div className="flex flex-col gap-1.5">
                <Typography variant="prompt" className="text-muted-foreground text-sm">
                  Protocol available
                </Typography>
                <Typography variant="p" className="font-semibold text-lg" gradient>
                  {USD_MOCK_TOKEN.format(marketInfo.facilitator.availableToBorrow, { style: 'compact' })}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5">
                <Typography variant="prompt" className="text-muted-foreground text-sm">
                  Borrow APY
                </Typography>
                <div className="flex items-center gap-2">
                  <Typography variant="p" className="font-semibold text-lg" gradient>
                    {formatPercentage(borrowAPY)}
                  </Typography>
                  {borrowPoints?.multiplier && (
                    <PointsBadge
                      asset={TokenSymbol(TokenSymbolEnum.USDXL)}
                      action="borrow"
                      multiplier={borrowPoints.multiplier}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            </div>
            <Button className="w-full sm:mx-auto sm:max-w-sm" onClick={connectWallet}>
              Connect Wallet to Borrow
            </Button>
          </div>
        )}
      </div>
    </Panel.Wrapper>
  )
}
